import { Download, Home, IdCard, Maximize, Minus, Plus, SlidersHorizontal, UserPlus } from 'lucide-react';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { lsGet, lsSet } from '../../model/db';
import { displayName, initials, isDeceased } from '../../model/people';
import type { Id, Person } from '../../model/types';
import { CARD_FIELDS, cardHeight, cardLines, cardText, DEFAULT_CARD_FIELDS, fitWidth, loadCardFields, nameWraps, saveCardFields, textLeft, type CardField } from '../chart/cards';
import { font } from './tableLayout';
import { edgePaths } from '../chart/edges';
import { CARD_W, layoutChart, type Collateral, type Layout, type LayoutOptions } from '../chart/layout';
import { useColorKey } from '../appearance';
import { roomForOverlays, usePanZoom, type Transform } from '../chart/usePanZoom';
import { openContextMenu } from '../components/ContextMenu';
import { avatarColor, Empty, Stepper } from '../components/common';
import { Popover, usePopover } from '../components/Popover';
import { Menu, type MenuEntry } from '../components/Menu';
import { focusPerson, openDialog, selectPerson, useApp } from '../store';
import { findTheme } from '../themes';

const OPTS_KEY = 'relationships.chartOptions';
const DEFAULT_OPTS: LayoutOptions = { ancestors: 3, descendants: 2, collateral: 'siblings', partners: true, maxPeople: 1500 };

function loadOpts(): LayoutOptions {
  try {
    return { ...DEFAULT_OPTS, ...JSON.parse(lsGet(OPTS_KEY) ?? '{}') };
  } catch {
    return DEFAULT_OPTS;
  }
}

export function TreeView() {
  const tree = useApp((s) => s.tree)!;
  const focusId = useApp((s) => s.focusId);
  const selectedId = useApp((s) => s.selectedId);
  const [opts, setOpts] = useState<LayoutOptions>(loadOpts);
  const [cardFields, setCardFields] = useState<CardField[]>(loadCardFields);
  const [cardMenu, setCardMenu] = useState<{ x: number; y: number } | null>(null);
  const showMenu = usePopover();
  const setFields = (next: CardField[]) => {
    setCardFields(next);
    saveCardFields(next);
  };
  const wrapRef = useRef<HTMLDivElement>(null);
  const { t, set, centerOn, fit, zoomAt, animateTo } = usePanZoom(wrapRef, { minK: 0.02, onBackgroundTap: () => selectPerson(null) });
  const [size, setSize] = useState({ w: 800, h: 600 });
  const stripes = findTheme(useApp((s) => s.settings.themeId)).stripes;

  const updateOpts = (patch: Partial<LayoutOptions>) => {
    const next = { ...opts, ...patch };
    setOpts(next);
    lsSet(OPTS_KEY, JSON.stringify(next));
  };

  const focus = focusId && tree.people[focusId] ? focusId : null;
  // Cards are one height across the tree; taller when some name needs two lines.
  const twoLineNames = useMemo(() => Object.values(tree.people).some((p) => nameWraps(displayName(p), cardFields.includes('photo'))), [tree, cardFields]);
  const cardH = cardHeight(cardFields, twoLineNames);
  const layout = useMemo(() => (focus ? layoutChart(tree, focus, { ...opts, cardH }) : null), [tree, focus, opts, cardH]);
  const CARD_H = layout?.cardH ?? cardH;

  // Keep the new focus person where it was on screen, then glide it to the center.
  const prev = useRef<{ layout: Layout | null; t: Transform }>({ layout: null, t });
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!layout || !focus || !el) return;
    const before = prev.current;
    const old = before.layout?.nodes.get(focus);
    const k = before.t.k;
    const target = { k, x: el.clientWidth / 2, y: el.clientHeight / 2 - (CARD_H / 2) * k };
    if (old && before.layout !== layout) {
      const sx = before.t.x + old.x * k;
      const sy = before.t.y + old.y * k;
      set({ k, x: sx, y: sy });
      animateTo(target);
    } else if (!before.layout || !old) {
      // First render, or a different tree/branch entirely: start fresh at a readable zoom.
      set({ k: 0.9, x: el.clientWidth / 2, y: el.clientHeight / 2 - (CARD_H / 2) * 0.9 });
    } else if (before.layout !== layout) animateTo(target);
    prev.current.layout = layout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);
  prev.current.t = t;

  // The chart (and so wrapRef) only mounts once there's someone to show, which can be after the first render.
  const hasChart = !!focus && !!layout;
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasChart]);

  if (!focus || !layout) {
    return (
      <Empty title="No one in this tree yet">
        <button className="btn primary" onClick={() => openDialog({ type: 'new-person' })}>
          <UserPlus size={16} /> Add the first person
        </button>
      </Empty>
    );
  }

  // Visible world rectangle (with margin) for culling.
  const margin = 200;
  const view = {
    x0: (-t.x - margin) / t.k,
    y0: (-t.y - margin) / t.k,
    x1: (size.w - t.x + margin) / t.k,
    y1: (size.h - t.y + margin) / t.k,
  };
  const nodes = [...layout.nodes.values()];
  const visible = nodes.filter((n) => n.x + CARD_W / 2 >= view.x0 && n.x - CARD_W / 2 <= view.x1 && n.y + CARD_H >= view.y0 && n.y <= view.y1);

  const onKey = (e: React.KeyboardEvent) => {
    // Keys pressed on the zoom buttons or legend inside the chart belong to those controls.
    if (e.target !== e.currentTarget) return;
    const cur = layout.nodes.get(selectedId && layout.nodes.has(selectedId) ? selectedId : focus)!;
    let next: Id | undefined;
    const sameRow = nodes.filter((n) => n.gen === cur.gen).sort((a, b) => a.x - b.x);
    const nearest = (cands: typeof nodes) => cands.sort((a, b) => Math.abs(a.x - cur.x) - Math.abs(b.x - cur.x))[0]?.id;
    if (e.key === 'ArrowLeft') next = sameRow[sameRow.findIndex((n) => n.id === cur.id) - 1]?.id;
    else if (e.key === 'ArrowRight') next = sameRow[sameRow.findIndex((n) => n.id === cur.id) + 1]?.id;
    else if (e.key === 'ArrowUp') next = nearest(nodes.filter((n) => n.gen === cur.gen - 1));
    else if (e.key === 'ArrowDown') next = nearest(nodes.filter((n) => n.gen === cur.gen + 1));
    else if (e.key === 'Enter') {
      focusPerson(cur.id);
      e.preventDefault();
      return;
    } else if (e.key === '+' || e.key === '=') zoomAt(size.w / 2, size.h / 2, 1.25, true);
    else if (e.key === '-') zoomAt(size.w / 2, size.h / 2, 0.8, true);
    else if (e.key === '0') fit(layout.bounds);
    else return;
    e.preventDefault();
    if (next) {
      selectPerson(next);
      const n = layout.nodes.get(next)!;
      const sx = t.x + n.x * t.k;
      const sy = t.y + n.y * t.k;
      if (sx < 80 || sx > size.w - 80 || sy < 60 || sy > size.h - 100) centerOn(n.x, n.y + CARD_H / 2);
    }
  };

  return (
    <>
      <div
        className="canvas-wrap"
        ref={wrapRef}
        tabIndex={0}
        onKeyDown={onKey}
        onContextMenu={(e) => {
          // People get the person menu from the global handler; empty space gets chart actions.
          if ((e.target as Element).closest('[data-person-id], [data-no-pan]')) return;
          const r = e.currentTarget.getBoundingClientRect();
          const sx = e.clientX - r.left;
          const sy = e.clientY - r.top;
          const home = tree.homePersonId && tree.people[tree.homePersonId] ? tree.homePersonId : undefined;
          openContextMenu(e, [
            { key: 'add', label: 'Add a person…', icon: <UserPlus size={14} />, hint: 'N', onSelect: () => openDialog({ type: 'new-person' }) },
            ...(home && home !== focus ? [{ key: 'home', label: 'Center on home person', icon: <Home size={14} />, onSelect: () => focusPerson(home) }] : []),
            'separator',
            { key: 'in', label: 'Zoom in here', icon: <Plus size={14} />, hint: '+', onSelect: () => zoomAt(sx, sy, 1.4, true) },
            { key: 'out', label: 'Zoom out', icon: <Minus size={14} />, hint: '−', onSelect: () => zoomAt(sx, sy, 0.7, true) },
            { key: 'fit', label: 'Fit everything', icon: <Maximize size={14} />, hint: '0', onSelect: () => fit(layout.bounds) },
            'separator',
            { key: 'export', label: 'Export chart as image or PDF…', icon: <Download size={14} />, onSelect: () => openDialog({ type: 'export', picture: true }) },
          ]);
        }}
        aria-label={`Family chart centered on ${displayName(tree.people[focus])}. Use arrow keys to move between people, Enter to center.`}
      >
        <svg role="presentation">
          <defs>
            <clipPath id="avatar-clip" clipPathUnits="objectBoundingBox">
              <circle cx={0.5} cy={0.5} r={0.5} />
            </clipPath>
            {/* Pride themes outline the focused person in the flag, every stripe of it (styles.css). */}
            {stripes && (
              <linearGradient id="flag-outline">
                {stripes.map((c, i) => [i, i + 1].map((at) => <stop key={`${i}-${at}`} offset={at / stripes.length} stopColor={c} />))}
              </linearGradient>
            )}
          </defs>
          <g transform={`translate(${t.x},${t.y}) scale(${t.k})`}>
            <Edges layout={layout} view={view} />
            {visible.map((n) => (
              <Card
                key={n.id}
                person={tree.people[n.id]}
                x={n.x}
                y={n.y}
                focus={n.id === focus}
                selected={n.id === selectedId}
                hiddenParents={n.hiddenParents}
                hiddenChildren={n.hiddenChildren}
                detail={t.k < 0.12 ? 'tiny' : t.k < 0.35 ? 'low' : 'full'}
                h={CARD_H}
                photo={cardFields.includes('photo')}
                lines={t.k < 0.35 ? '' : cardLines(tree, tree.people[n.id], cardFields).join('\n')}
              />
            ))}
          </g>
        </svg>
        {/* Options float over the chart: a summary button for what's shown, and the rest one tap away. */}
        <div className="chart-bar" role="toolbar" aria-label="Chart options" data-no-pan>
          <button className="btn small chart-btn" aria-haspopup="dialog" aria-expanded={showMenu.open} onClick={showMenu.toggle} title="Generations and relatives shown">
            <SlidersHorizontal size={14} />
            <span className="chart-btn-text">
              {opts.ancestors} up · {opts.descendants} down · {COLLATERAL_SHORT[opts.collateral]}
              {!opts.partners && ' · no partners'}
            </span>
          </button>
          <button
            className="btn small chart-btn"
            aria-haspopup="menu"
            aria-expanded={!!cardMenu}
            aria-label="Cards"
            title="Choose what each card shows"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setCardMenu(cardMenu ? null : { x: r.left, y: r.bottom + 4 });
            }}
          >
            <IdCard size={14} /> <span className="hide-sm">Cards</span>
          </button>
          <span className="spacer" />
          {tree.homePersonId && tree.people[tree.homePersonId] && tree.homePersonId !== focus && (
            <button className="btn small chart-btn" onClick={() => focusPerson(tree.homePersonId!)} aria-label="Center on the home person" title="Center on the home person">
              <Home size={14} /> <span className="hide-sm">Home</span>
            </button>
          )}
          <button className="btn small chart-btn" onClick={() => openDialog({ type: 'new-person' })} aria-label="Add person" title="Add a person (N)">
            <UserPlus size={14} /> <span className="hide-sm">Add person</span>
          </button>
        </div>
        {cardMenu && (
          <Menu
            ariaLabel="What cards show"
            x={cardMenu.x}
            y={cardMenu.y}
            onClose={() => setCardMenu(null)}
            items={[
              { key: 'name', label: 'Name', checked: true, disabled: true, keepOpen: true },
              ...CARD_FIELDS.flatMap((f, i): MenuEntry[] => [
                ...(i > 0 && CARD_FIELDS[i - 1].group !== f.group ? ['separator' as const] : []),
                {
                  key: f.key,
                  label: f.label,
                  hint: f.key === 'relation' && !(tree.homePersonId && tree.people[tree.homePersonId]) ? 'needs a home person' : undefined,
                  checked: cardFields.includes(f.key),
                  keepOpen: true,
                  onSelect: () => setFields(cardFields.includes(f.key) ? cardFields.filter((k) => k !== f.key) : [...cardFields, f.key]),
                },
              ]),
              'separator',
              { key: 'reset', label: 'Reset to default', onSelect: () => setFields(DEFAULT_CARD_FIELDS) },
            ]}
          />
        )}
        {showMenu.open && (
          <Popover anchor={showMenu.anchor!} onClose={showMenu.close} ariaLabel="What the chart shows">
            <div>
              <div className="popover-label">Generations</div>
              <div className="row" style={{ gap: 10 }}>
                <span className="row" style={{ gap: 6 }}>
                  Up <Stepper label="Generations up" value={opts.ancestors} onChange={(ancestors) => updateOpts({ ancestors })} min={0} max={15} />
                </span>
                <span className="row" style={{ gap: 6 }}>
                  Down <Stepper label="Generations down" value={opts.descendants} onChange={(descendants) => updateOpts({ descendants })} min={0} max={15} />
                </span>
              </div>
            </div>
            <div role="radiogroup" aria-label="Relatives">
              <div className="popover-label">Relatives</div>
              {COLLATERAL_OPTIONS.map((o) => (
                <label key={o.value} className="choice">
                  <input type="radio" name="collateral" checked={opts.collateral === o.value} onChange={() => updateOpts({ collateral: o.value })} />
                  {o.label}
                </label>
              ))}
            </div>
            <label className="check">
              <input type="checkbox" checked={opts.partners} onChange={(e) => updateOpts({ partners: e.target.checked })} /> Show partners
            </label>
            <div className="popover-foot">{layout.nodes.size.toLocaleString('en-US')} people shown</div>
          </Popover>
        )}
        {layout.truncated && <div className="chart-note">Showing the first {opts.maxPeople.toLocaleString('en-US')} people. Reduce generations to see the rest clearly.</div>}
        {roomForOverlays(size) && <Legend />}
        {roomForOverlays(size) && <Minimap layout={layout} t={t} size={size} onMove={(wx, wy, animate) => centerOn(wx, wy, undefined, animate)} />}
        <div className="zoom-controls" data-no-pan>
          <button className="btn icon small ghost" onClick={() => zoomAt(size.w / 2, size.h / 2, 1.25, true)} aria-label="Zoom in" title="Zoom in (+)">
            <Plus size={16} />
          </button>
          <button className="btn icon small ghost" onClick={() => zoomAt(size.w / 2, size.h / 2, 0.8, true)} aria-label="Zoom out" title="Zoom out (−)">
            <Minus size={16} />
          </button>
          <button className="btn icon small ghost" onClick={() => fit(layout.bounds)} aria-label="Fit everything" title="Fit everything (0)">
            <Maximize size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

const COLLATERAL_OPTIONS: { value: Collateral; label: string }[] = [
  { value: 'direct', label: 'Direct line only' },
  { value: 'siblings', label: 'Siblings, aunts, and uncles' },
  { value: 'all', label: 'Everyone, including cousins' },
];
/** How the options button sums up which relatives are shown. */
const COLLATERAL_SHORT: Record<Collateral, string> = { direct: 'direct line', siblings: 'siblings', all: 'everyone' };

const Card = memo(function Card({
  person,
  x,
  y,
  focus,
  selected,
  hiddenParents,
  hiddenChildren,
  detail,
  h: CARD_H,
  photo,
  lines,
}: {
  person: Person;
  x: number;
  y: number;
  focus: boolean;
  selected: boolean;
  hiddenParents: boolean;
  hiddenChildren: boolean;
  /** Less text when zoomed far out: readable at a glance and much cheaper to render. */
  detail: 'full' | 'low' | 'tiny';
  /** Card height (taller with more details). */
  h: number;
  /** Show the photo or initials. */
  photo: boolean;
  /** The detail lines under the name (cards.ts), joined with newlines so the card only redraws when they change. */
  lines: string;
}) {
  const name = displayName(person);
  const textX = textLeft(photo);
  const detailLines = lines ? lines.split('\n') : [];
  const text = cardText(name, detailLines, photo, CARD_H);
  // Tall cards keep the photo near the top, beside the name.
  const avatarY = Math.min(CARD_H / 2, text.nameY[0] + 12);
  const living = !isDeceased(person);
  const cls = ['card', focus && 'focus', selected && 'selected', !living && 'deceased'].filter(Boolean).join(' ');
  return (
    <g
      className={cls}
      data-node={person.id}
      data-person-id={person.id}
      transform={`translate(${x - CARD_W / 2},${y})`}
      onClick={() => selectPerson(person.id)}
      onDoubleClick={() => focusPerson(person.id)}
    >
      <title>{[name, ...detailLines, 'Click for details, double-click to center'].join('\n')}</title>
      <rect className="bg" width={CARD_W} height={CARD_H} rx={10} />
      {detail === 'tiny' ? null : detail === 'low' ? (
        <text className="name" x={CARD_W / 2} y={CARD_H / 2 + 8} textAnchor="middle" style={{ fontSize: 22 }}>
          {fitWidth(name, CARD_W - 20, font(22, 650))}
        </text>
      ) : (
        <>
          {!photo ? null : person.photo ? (
            <image href={person.photo} x={8} y={avatarY - 16} width={32} height={32} clipPath="url(#avatar-clip)" preserveAspectRatio="xMidYMid slice" />
          ) : (
            <>
              <circle cx={24} cy={avatarY} r={16} fill={avatarColor(person.id)} />
              <text className="initials" x={24} y={avatarY + 4} textAnchor="middle">
                {initials(person)}
              </text>
            </>
          )}
          {text.name.map((line, i) => (
            <text key={`n${i}`} className="name" x={textX} y={text.nameY[i]}>
              {line}
            </text>
          ))}
          {text.details.map((line, i) => (
            <text key={i} className={i === 0 ? 'years' : 'pron'} x={textX} y={text.detailY[i]}>
              {line}
            </text>
          ))}
        </>
      )}
      {hiddenParents && detail !== 'tiny' && (
        <g
          className="more"
          transform={`translate(${CARD_W / 2},-11)`}
          onClick={(e) => {
            e.stopPropagation();
            focusPerson(person.id);
          }}
        >
          <title>More ancestors: center here</title>
          <circle r={9} />
          <path d="M-4 2 L0 -2 L4 2" />
        </g>
      )}
      {hiddenChildren && detail !== 'tiny' && (
        <g
          className="more"
          transform={`translate(${CARD_W / 2},${CARD_H + 11})`}
          onClick={(e) => {
            e.stopPropagation();
            focusPerson(person.id);
          }}
        >
          <title>More descendants: center here</title>
          <circle r={9} />
          <path d="M-4 -2 L0 2 L4 -2" />
        </g>
      )}
    </g>
  );
});

function Edges({ layout, view }: { layout: Layout; view: { x0: number; y0: number; x1: number; y1: number } }) {
  const paths = useMemo(() => edgePaths(layout), [layout]);
  return (
    <g>
      {paths
        .filter(({ box: [x0, y0, x1, y1] }) => x1 >= view.x0 && x0 <= view.x1 && y1 >= view.y0 && y0 <= view.y1)
        .map((p) => (
          <path key={p.key} className={p.cls} d={p.d} />
        ))}
    </g>
  );
}

function Legend() {
  const [open, setOpen] = useState(() => lsGet('relationships.legend') !== 'closed');
  const item = (cls: string, label: string) => (
    <div className="legend-item">
      <svg viewBox="0 0 28 8">
        <line className={cls} x1={0} y1={4} x2={28} y2={4} />
      </svg>
      {label}
    </div>
  );
  if (!open)
    return (
      <button className="btn small legend" style={{ padding: '4px 10px' }} onClick={() => { setOpen(true); lsSet('relationships.legend', 'open'); }}>
        Legend
      </button>
    );
  return (
    <div className="legend" aria-label="Line legend" data-no-pan>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <b className="small-text">Lines</b>
        <button className="btn ghost small" style={{ minHeight: 20, padding: '0 4px' }} onClick={() => { setOpen(false); lsSet('relationships.legend', 'closed'); }} aria-label="Hide legend">
          Hide
        </button>
      </div>
      {item('edge', 'Parent & child')}
      {item('edge adoptive', 'Adoptive')}
      {item('edge step', 'Step-parent')}
      {item('edge foster', 'Foster / guardian')}
      {item('edge chosen', 'Chosen family')}
      {item('edge partner', 'Partners')}
      {item('edge partner ended', 'Former partners')}
    </div>
  );
}

function Minimap({ layout, t, size, onMove }: { layout: Layout; t: Transform; size: { w: number; h: number }; onMove: (wx: number, wy: number, animate: boolean) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const colorKey = useColorKey();
  const W = 180;
  const H = 120;
  const b = layout.bounds;
  const pad = 8;
  const scale = Math.min((W - pad * 2) / Math.max(1, b.maxX - b.minX), (H - pad * 2) / Math.max(1, b.maxY - b.minY));
  const ox = pad + (W - pad * 2 - (b.maxX - b.minX) * scale) / 2 - b.minX * scale;
  const oy = pad + (H - pad * 2 - (b.maxY - b.minY) * scale) / 2 - b.minY * scale;
  // The viewport rectangle, in minimap pixels.
  const vx = (-t.x / t.k) * scale + ox;
  const vy = (-t.y / t.k) * scale + oy;
  const vw = (size.w / t.k) * scale;
  const vh = (size.h / t.k) * scale;
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = W * dpr;
    c.height = H * dpr;
    const ctx = c.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);
    const css = getComputedStyle(c);
    ctx.fillStyle = css.getPropertyValue('--text-faint') || '#999';
    for (const n of layout.nodes.values()) ctx.fillRect(ox + (n.x - CARD_W / 2) * scale, oy + n.y * scale, Math.max(1.5, CARD_W * scale), Math.max(1.5, layout.cardH * scale));
    ctx.strokeStyle = css.getPropertyValue('--accent') || '#5b4fd6';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(vx, vy, vw, vh);
  }, [layout, vx, vy, vw, vh, scale, ox, oy, colorKey]);
  if (layout.nodes.size < 12) return null;

  const local = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { mx: e.clientX - r.left, my: e.clientY - r.top };
  };
  // World point under the pointer, plus the drag offset so a grabbed viewport doesn't jump.
  const moveTo = (mx: number, my: number, animate: boolean) => {
    const d = drag.current ?? { dx: 0, dy: 0 };
    onMove((mx - ox) / scale + d.dx, (my - oy) / scale + d.dy, animate);
  };
  return (
    <canvas
      ref={ref}
      className="minimap"
      data-no-pan
      style={{ width: W, height: H }}
      aria-label="Overview map. Click or drag to move around."
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        const { mx, my } = local(e);
        const inside = mx >= vx && mx <= vx + vw && my >= vy && my <= vy + vh;
        // Grabbing the viewport drags it from where it was grabbed; anywhere else jumps there first.
        drag.current = inside ? { dx: (vx + vw / 2 - mx) / scale, dy: (vy + vh / 2 - my) / scale } : { dx: 0, dy: 0 };
        moveTo(mx, my, !inside);
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const { mx, my } = local(e);
        moveTo(mx, my, false);
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    />
  );
}
